// ---------------------------------------------------------------------------
// AppLock — IndexedDB storage backend
// ---------------------------------------------------------------------------
//
// Wraps IndexedDB as a simple key-value store using a single object store.
// All values are stored as strings (matching the localStorage contract).
//
// The database is opened lazily on first access and kept open for the
// lifetime of the backend instance. Call `close()` during teardown.
//
// ---------------------------------------------------------------------------
/** Default IndexedDB database name. */
const DEFAULT_DB_NAME = 'applock';
/** Default object store name. */
const DEFAULT_STORE_NAME = 'kv';
/** Current IndexedDB schema version. */
const DB_VERSION = 1;
/**
 * IndexedDB wrapped as a `StorageBackend`.
 *
 * Uses a single object store (`kv` by default) with out-of-line keys.
 * The database is opened lazily on first use and reused for all
 * subsequent operations.
 *
 * Key design decisions:
 * - All values are strings (serialization is handled upstream).
 * - Each operation runs in its own transaction for simplicity.
 * - The `close()` method must be called during teardown to release
 *   the database connection.
 */
export class IDBBackend {
    constructor(dbName = DEFAULT_DB_NAME, storeName = DEFAULT_STORE_NAME) {
        this.dbName = dbName;
        this.storeName = storeName;
        this.dbPromise = null;
    }
    // -----------------------------------------------------------------------
    // StorageBackend implementation
    // -----------------------------------------------------------------------
    async getItem(key) {
        const db = await this.open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readonly');
            const store = tx.objectStore(this.storeName);
            const req = store.get(key);
            req.onsuccess = () => {
                const value = req.result;
                resolve(value === undefined ? null : value);
            };
            req.onerror = () => reject(req.error);
        });
    }
    async setItem(key, value) {
        const db = await this.open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            const store = tx.objectStore(this.storeName);
            const req = store.put(value, key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }
    async removeItem(key) {
        const db = await this.open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            const store = tx.objectStore(this.storeName);
            const req = store.delete(key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }
    async getAllKeys() {
        const db = await this.open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readonly');
            const store = tx.objectStore(this.storeName);
            const req = store.getAllKeys();
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    }
    // -----------------------------------------------------------------------
    // Lifecycle
    // -----------------------------------------------------------------------
    /**
     * Close the database connection.
     * Should be called during `AppLock.destroy()` to release the connection.
     */
    close() {
        if (this.dbPromise) {
            this.dbPromise.then((db) => db.close()).catch(() => {
                // Swallow — already closed or failed to open
            });
            this.dbPromise = null;
        }
    }
    // -----------------------------------------------------------------------
    // Internal
    // -----------------------------------------------------------------------
    /**
     * Open (or reuse) the IndexedDB database connection.
     * Creates the object store on first open via `onupgradeneeded`.
     */
    open() {
        if (!this.dbPromise) {
            this.dbPromise = new Promise((resolve, reject) => {
                const request = indexedDB.open(this.dbName, DB_VERSION);
                request.onupgradeneeded = () => {
                    const db = request.result;
                    if (!db.objectStoreNames.contains(this.storeName)) {
                        db.createObjectStore(this.storeName);
                    }
                };
                request.onsuccess = () => resolve(request.result);
                request.onerror = () => reject(request.error);
            });
        }
        return this.dbPromise;
    }
}
//# sourceMappingURL=idb-backend.js.map